import { Icon } from "../Icons";

export function Pricing({ onBuyClick, onDownloadClick, onContactClick }: { onBuyClick: () => void; onDownloadClick: () => void; onContactClick: () => void }) {
  return (
    <section className="section" id="pricing" style={{ background: "var(--bg-subtle)" }}>
      <div className="section-inner">
        <div className="section-header">
          <div className="eyebrow">06 · Pricing</div>
          <h2>Free to try. Pay when you ship.</h2>
          <p className="lede">
            One free trial, one commercial license. No quote flow for a single gateway.
          </p>
        </div>

        <div
          className="pricing-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 20,
            maxWidth: 960,
            margin: "0 auto",
            alignItems: "stretch",
          }}
        >
          {/* Free */}
          <div className="card pricing-card" style={{ padding: "32px 32px 28px", background: "#fff" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <div style={{ fontSize: 22, fontWeight: 500, letterSpacing: "-0.01em" }}>
                Free trial
              </div>
              <span className="chip" style={{ marginLeft: "auto" }}>
                no email required
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 20 }}>
              <div
                style={{ fontSize: 48, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1 }}
              >
                $0
              </div>
              <div style={{ fontSize: 13, color: "var(--ink-3)" }}>&nbsp;</div>
            </div>
            <p style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 14, lineHeight: 1.5 }}>
              Install the module, author SQL, deploy processors. Run as long as you want.{" "}
              <span style={{ color: "var(--ink)", fontWeight: 500 }}>
                Processors stop every 2 hours
              </span>
              , just like Ignition's trial. Restart and keep going.
            </p>
            <div
              className="pricing-features"
              style={{
                borderTop: "1px solid var(--line)",
                marginTop: 22,
                paddingTop: 18,
                display: "grid",
                gap: 10,
                fontSize: 13,
              }}
            >
              {[
                "Full SQL surface: ~50 operators",
                "Per-identity processors, live testing, operations dashboard",
                "No telemetry or cloud processing",
              ].map((t, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <Icon.Check style={{ color: "var(--ok)", marginTop: 2, flex: "0 0 14px" }} />
                  <span style={{ color: "var(--ink-2)" }}>{t}</span>
                </div>
              ))}
            </div>
            <button
              onClick={onDownloadClick}
              className="btn btn-secondary pricing-cta"
              style={{ marginTop: 24, width: "100%", justifyContent: "center" }}
            >
              <Icon.Download /> Download
            </button>
          </div>

          {/* Paid */}
          <div
            className="card pricing-card"
            style={{
              padding: "32px 32px 28px",
              background: "#fff",
              position: "relative",
              borderColor: "var(--ink-4)",
            }}
          >
            <div style={{ position: "absolute", top: -10, right: 20 }}>
              <span
                className="chip"
                style={{
                  background: "var(--accent)",
                  color: "#1a1708",
                  border: "1px solid rgba(0,0,0,.12)",
                  fontWeight: 600,
                }}
              >
                Commercial
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <div style={{ fontSize: 22, fontWeight: 500, letterSpacing: "-0.01em" }}>
                Commercial license
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 20 }}>
              <div
                style={{ fontSize: 48, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1 }}
              >
                $6,000
              </div>
              <div style={{ fontSize: 13, color: "var(--ink-3)" }}>/ gateway</div>
            </div>
            <p style={{ fontSize: 14, color: "var(--ink-2)", marginTop: 14, lineHeight: 1.5 }}>
              Unlocks continuous runtime. Includes the first year of updates and support; annual
              maintenance renewal is 20%. Checkout collects the Ignition Gateway ID so Katenaria
              can issue the gateway-bound license key by email.
            </p>
            <div
              className="pricing-features"
              style={{
                borderTop: "1px solid var(--line)",
                marginTop: 22,
                paddingTop: 18,
                display: "grid",
                gap: 10,
                fontSize: 13,
              }}
            >
              {[
                "No 2-hour restart interval. Runs continuously.",
                "Ignition Gateway ID collected at checkout for license binding",
                "Installed license runs offline; no phone-home verification",
              ].map((t, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <Icon.Check style={{ color: "var(--ok)", marginTop: 2, flex: "0 0 14px" }} />
                  <span style={{ color: "var(--ink-2)" }}>{t}</span>
                </div>
              ))}
            </div>
            <button
              className="btn btn-primary pricing-cta"
              onClick={onBuyClick}
              style={{ marginTop: 24, width: "100%", justifyContent: "center" }}
            >
              Buy a license <Icon.External />
            </button>
          </div>
        </div>

        <div
          style={{ textAlign: "center", marginTop: 28, fontSize: 13, color: "var(--ink-3)" }}
        >
          Need a volume / multi-site / OEM arrangement?{" "}
          <button onClick={onContactClick} className="lnk" style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "inherit", color: "inherit" }}>
            Contact us
          </button>
        </div>

        <div
          className="support-strip"
          style={{
            marginTop: 42,
            borderTop: "1px solid var(--line)",
            paddingTop: 28,
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 24,
          }}
        >
          {(
            [
              [
                "First workload support",
                "A short sizing or adaptation session is included for customers when it is enough to unblock the first processor.",
              ],
              [
                "Integrator friendly",
                "We can work directly with your Ignition system integrator, including certified partners already responsible for the gateway.",
              ],
              [
                "Data science ready",
                "When a workload needs data ingestion, model adaptation, or a site-specific algorithm, we scope that as a separate paid service at market rate.",
              ],
            ] as Array<[string, string]>
          ).map(([title, body], i) => (
            <div key={i} className="support-item">
              <div className="eyebrow" style={{ color: "var(--ink-2)", marginBottom: 8 }}>
                {title}
              </div>
              <div style={{ fontSize: 13.5, color: "var(--ink-3)", lineHeight: 1.55 }}>{body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
